import { DownloadSimpleIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useExportChecklists } from '@/api/checklists/export-checklists'
import { GetChecklistsRequest, useGetChecklists } from '@/api/checklists/get-checklists'
import { getChecklistsResidents } from '@/api/checklists/get-checklists-residents'
import { getChecklistsTemplates } from '@/api/checklists/get-checklists-templates'
import ChecklistsAssignTaskModal from '@/components/BuildingDashboard/Checklists/ChecklistsAssignTaskModal/ChecklistsAssignTaskModal'
import ChecklistsCreateNewTaskModal from '@/components/BuildingDashboard/Checklists/ChecklistsCreateNewTaskModal/ChecklistsCreateNewTaskModal'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select, useSelectOptions } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants/date'
import { popupFactory, routerFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models'
import { Checklist, ChecklistStatus, ChecklistType, ChecklistTypeEnum } from '@/models/features'
import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  makeChecklistTypeOptions,
} from '@/pages/checklists/checklists-dashboard/checklists-dashboard.page.constants'
import {
  ChecklistStatusExtended,
  ChecklistStatusExtendedEnum,
} from '@/pages/checklists/checklists-dashboard/checklists-dashboard.page.types'
import {
  getChecklistStatusColor,
  getChecklistStatusLabel,
} from '@/pages/checklists/checklists-dashboard/checklists-dashboard.page.utils'
import { useChecklistsBulkActions } from '@/pages/checklists/checklists-dashboard/use-checklists-bulk-actions'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { useUnitAssignableResidents } from '../unit-details.hooks'
import {
  getUnitChecklistsColumns,
  UNIT_CHECKLISTS_TABLE_ID,
  unitChecklistsFiltersSchema,
  unitChecklistsInitialFilters,
} from './unit-checklists.page.constants'

const STATUS_TABS: ChecklistStatus[] = ['not-started', 'pending-review', 'past-due', 'completed']

export const UnitChecklistsPage = () => {
  const { t } = useTranslation(['units'])
  const { t: tChecklists } = useTranslation(['checklists'])
  const { unitId } = useParams<{ unitId: string }>()
  const { navigate } = routerFactory.useRouter()
  const building = useSelector(selectActiveBuilding)
  const { isLoading: isResidentsLoading, options: assignableResidents } =
    useUnitAssignableResidents(unitId!)
  const timezone = building?.timezone ?? DEFAULT_TZ

  const searchParams = useSearchParams({
    initialState: unitChecklistsInitialFilters,
    schema: unitChecklistsFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: unitChecklistsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { filters: filtersState } = filters
  const { limit, search, skip, sortBy, sortOrder, status = 'all' } = filtersState

  const queryParams: GetChecklistsRequest = {
    buildingId: building?.id,
    clientId: filtersState.clientId,
    fromDate: filtersState.fromDate,
    limit,
    parentChecklistId: filtersState.parentChecklistId,
    search,
    skip,
    sortBy,
    sortOrder,
    status: status === 'all' ? undefined : (status as ChecklistStatus),
    toDate: filtersState.toDate,
    type: filtersState.type,
    unitId: Number(unitId),
  }

  const { data, isFetching, error } = useGetChecklists(queryParams)
  const checklists = data?.data ?? []
  const total = data?.total ?? 0
  const countByStatus = data?.countByStatus

  const residentsOptions = useSelectOptions(
    {
      query: getChecklistsResidents,
      requestData: { ...queryParams, buildingId: building?.id },
      transform: (residentsData) =>
        (residentsData?.data ?? []).map((client) => ({
          label: `${client.name} - (${client.count})`,
          value: client.clientId,
        })),
    },
    { enabled: !!building?.id, queryKey: ['getChecklistsResidents', building?.id, unitId] },
  )

  const templatesOptions = useSelectOptions(
    {
      query: getChecklistsTemplates,
      requestData: { buildingId: building?.id },
      transform: (templatesData) =>
        (templatesData?.data ?? []).map((template) => ({
          label: template.name,
          value: template.id,
        })),
    },
    { enabled: !!building?.id, queryKey: ['getChecklistsTemplates', building?.id] },
  )

  const { mutateAsync: downloadChecklists } = useExportChecklists()
  const bulkActions = useChecklistsBulkActions({ building, statusTab: status })

  const statusTabsOptions: StatusTabOption<ChecklistStatusExtended>[] = countByStatus
    ? [
        {
          count: Object.values(countByStatus).reduce((acc, curr) => acc + curr, 0),
          label: tChecklists('checklists:dashboard.status.all'),
          value: ChecklistStatusExtendedEnum.enum.all,
        },
        ...STATUS_TABS.map((tab) => ({
          color: getChecklistStatusColor(tab),
          count: countByStatus[tab] || 0,
          label: getChecklistStatusLabel(tab, tChecklists),
          value: tab,
        })),
      ]
    : []

  const dateRange: DateRange = [
    filtersState.fromDate ? new Date(filtersState.fromDate) : null,
    filtersState.toDate ? new Date(filtersState.toDate) : null,
  ]

  const rowActions: RowAction<Checklist>[] = [
    {
      label: tChecklists('checklists:dashboard.actions.view'),
      onClick: (row) => {
        // Note: same routing as the main checklists dashboard - inspections still use the old view.
        if (row.type === ChecklistTypeEnum.enum.inspection) {
          navigate({
            params: { inspectionId: row.id },
            path: '/dashboard/building-dashboard/checklists/inspection/:inspectionId',
          })
          return
        }

        navigate({
          params: { taskId: row.id },
          path: '/dashboard/building-dashboard/checklists/task/:taskId',
        })
      },
      primary: true,
    },
  ]

  const handleAssignTask = () => {
    popupFactory.open(ChecklistsAssignTaskModal, {
      building,
      residentOptions: assignableResidents,
      showCreateNewTask: () => popupFactory.open(ChecklistsCreateNewTaskModal, {}),
    })
  }

  const handleDownloadCSV = () => {
    if (!building) return

    void downloadChecklists({
      fileName: `${building.name?.trim() ? `${building.name.trim()}-` : ''}checklists.csv`,
      ...queryParams,
      buildingId: building.id,
    })
  }

  const handleSort = (newSortBy: string | null, newSortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: newSortBy ?? undefined,
      sortOrder: newSortOrder ?? undefined,
    })
  }

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({ fromDate: range[0]?.getTime(), toDate: range[1]?.getTime() })
  }

  return (
    <TableGroup
      noShadow
      id={UNIT_CHECKLISTS_TABLE_ID}
      columns={getUnitChecklistsColumns({ t: tChecklists, timezone })}
      data={checklists}
      error={error}
      getRowKey={(checklist) => checklist.id}
      loading={!building || isFetching}
      mode="bulk"
      bulkActions={bulkActions}
      rowActions={rowActions}
      title={
        <Typography as="h2" size="heading-small" color="black800">
          {t('units:checklists.title')}
        </Typography>
      }
      titleActions={
        <Button
          size="small"
          disabled={isResidentsLoading || !assignableResidents.length}
          onClick={handleAssignTask}
        >
          {t('units:detail.actions.assignTask')}
          <Icon icon={PlusIcon} />
        </Button>
      }
      emptyNoFilters={{
        description: t('units:checklists.empty.description'),
        title: t('units:checklists.empty.title'),
      }}
      filters={
        <>
          <Select
            {...templatesOptions.selectProps}
            searchable
            size="small"
            value={filtersState.parentChecklistId ?? []}
            type="multi"
            placeholder={tChecklists('checklists:dashboard.filters.namePlaceholder')}
            dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
            onChange={(ids: number[]) => filters.setFilter('parentChecklistId', ids)}
            onReset={() => filters.setFilter('parentChecklistId', [])}
          />
          <Select
            {...residentsOptions.selectProps}
            searchable
            size="small"
            type="multi"
            value={filtersState.clientId ?? []}
            placeholder={tChecklists('checklists:dashboard.filters.residentPlaceholder')}
            dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
            onChange={(ids: number[]) => filters.setFilter('clientId', ids)}
            onReset={() => filters.setFilter('clientId', [])}
          />
          <Select
            options={makeChecklistTypeOptions(tChecklists)}
            size="small"
            value={filtersState.type ?? null}
            placeholder={tChecklists('checklists:dashboard.filters.typePlaceholder')}
            onChange={(type: ChecklistType) => filters.setFilter('type', type)}
            onReset={() => filters.setFilter('type', undefined)}
          />
        </>
      }
      navigation={{
        actions: (
          <Button
            onlyBox
            size="small"
            use="secondary"
            title={tChecklists('checklists:dashboard.actions.exportCsv')}
            onClick={handleDownloadCSV}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        datePicker: (
          <DatePickerInput
            mode="range"
            placeholder={tChecklists('checklists:dashboard.filters.dateRangePlaceholder')}
            size="small"
            value={dateRange}
            maxDate={toZonedTime(new Date(), timezone)}
            onRangeChange={handleDateChange}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('units:checklists.filters.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => filters.setFilter('search', e.target.value)}
          />
        ),
        statusTabs: (
          <StatusTabs
            options={statusTabsOptions}
            value={status}
            disabled={isFetching}
            onChange={(value: ChecklistStatusExtended) => filters.setFilter('status', value)}
          />
        ),
      }}
      pagination={{ paginationFilters: filters, total }}
      tableFilters={filters}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
    />
  )
}
